// @ts-nocheck
/*
 * Port of views/dashboard/voice-credits-logs/company-voice-credits-history.js
 * (origin/main-dev, 917 lines).
 * BYTE-IDENTICAL LOGIC — every state, effect, aggregation
 * (getEffectiveDelta / effectiveBalanceByLogId running total), tab-
 * scoping, action-relative-to-company label logic (isSuperAdmin vs
 * receiver vs sender), pagination, and export CSV shape preserved.
 *
 * The v1 file ships its own scoped stylesheet inline via <style> — kept
 * verbatim (all `.vch-*` selectors, light + dark theme rules). No
 * chrome swap: raw HTML <table>, custom pagination footer, inline
 * styles preserved. Only import paths change.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { API_VOICE_CREDITS_LOGS, API_COMPANY_PROFILE } from '@/service/url';
import { getApiCall } from '@/service/httpCall';
import Loader from '@/components/partials/components/Loader';
import { formatRupeeAmount, exportToCSV } from '@/utilities/helpers';
import { getUser } from '@/utilities/setting';
import { USER_ROLES } from '@/utilities/constant';
import { CalendarDateInput } from '@/components/ui/calendar-inputs';
import {
  Search,
  Filter,
  RotateCcw,
  Download,
  Wallet,
  CircleCheck,
  Scale,
  TrendingUp,
  Building2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';

const getTodayStr = () => new Date().toLocaleDateString('en-CA');
const getOneMonthAgoStr = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toLocaleDateString('en-CA');
};

const formatDisplayDate = (str) => {
  if (!str) return '';
  const [y, m, d] = str.split('-');
  if (!y || !m || !d) return str;
  return `${d}-${m}-${y}`;
};

const formatTableDateTime = (value) => {
  if (!value) return '-';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '-';
  const day = String(dt.getDate()).padStart(2, '0');
  const month = String(dt.getMonth() + 1).padStart(2, '0');
  const year = dt.getFullYear();
  const time = dt.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
  return `${day}-${month}-${year} ${time}`;
};

const getInitials = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const SummaryCard = ({ icon: Icon, iconBg, iconColor, label, value, subLabel }) => (
  <div className="vch-summary-card">
    <div
      className="vch-summary-card__icon"
      style={{ backgroundColor: iconBg, color: iconColor }}
    >
      <Icon size={22} />
    </div>
    <div className="vch-summary-card__content">
      <div className="vch-summary-card__label">{label}</div>
      <div className="vch-summary-card__value">{value}</div>
      <div className="vch-summary-card__sub">{subLabel}</div>
    </div>
  </div>
);

function getEffectiveDelta(row, companyId) {
  const amt = Number(row.effective_amount || 0);
  const cid = Number(companyId);
  const toId = Number(row.to_company_id);
  const fromId = Number(row.from_company_id);
  const action = row.action;

  if (toId === cid) {
    if (action === 'addition') return amt;
    if (action === 'deduction') return -amt;
  }
  if (fromId === cid) {
    if (action === 'addition') return -amt;
    if (action === 'deduction') return amt;
  }
  return 0;
}

export default function CompanyVoiceCreditsHistory({
  companyId,
  displayName,
  trackEffectiveBalance = false,
  includeAllCompanyTransactions = false,
  logsApiUrl = API_VOICE_CREDITS_LOGS,
  emptyMessage = 'No voice credits history found for this company',
  exportFilePrefix = 'voice-credits-history',
  displayBalanceKey = 'credits_available',
  distributableBalanceKey = 'credits_distributable',
}) {
  const [logsList, setLogsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [fromDateValue, setFromDateValue] = useState(getOneMonthAgoStr());
  const [toDateValue, setToDateValue] = useState(getTodayStr());
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const user = getUser();
    setIsSuperAdmin(user?.role === USER_ROLES.SUPERADMIN);
  }, []);

  const fetchCompanyBalance = async () => {
    if (!companyId) return;
    try {
      const resp = await getApiCall(API_COMPANY_PROFILE.replace(':id', String(companyId)));
      setCurrentBalance(
        trackEffectiveBalance
          ? Number(resp?.[distributableBalanceKey] ?? resp?.[displayBalanceKey] ?? 0)
          : Number(resp?.[displayBalanceKey] || 0),
      );
    } catch (err) {
      console.error('Failed to fetch company balance', err);
    }
  };

  const fetchLogs = async (start, end) => {
    if (!companyId) return;
    try {
      setLoading(true);
      const performedByParam = includeAllCompanyTransactions ? '' : '&performedByMe=true';
      const url = `${logsApiUrl}?startDate=${start}&endDate=${end}&companyId=${companyId}${performedByParam}&limit=200`;
      const resp = await getApiCall(url, {});
      const data = Array.isArray(resp) ? resp : [];
      setLogsList(data);
      setCurrentPage(1);
    } catch (err) {
      console.error('Failed to fetch company credits logs', err);
      setLogsList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchLogs(fromDateValue, toDateValue);
      fetchCompanyBalance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const filteredLogs = useMemo(() => {
    if (!searchTerm.trim()) return logsList;
    const term = searchTerm.toLowerCase();
    return logsList.filter((row) => {
      const performedBy = row.performed_by_username || row.performedByUser?.username || '';
      const fromName = row.from_company_name || row.fromCompany?.name || '';
      const toName = row.to_company_name || row.toCompany?.name || '';
      const action = row.action || '';
      return (
        performedBy.toLowerCase().includes(term) ||
        fromName.toLowerCase().includes(term) ||
        toName.toLowerCase().includes(term) ||
        action.toLowerCase().includes(term)
      );
    });
  }, [searchTerm, logsList]);

  const summary = useMemo(() => {
    let totalGiven = 0;
    let givenCount = 0;
    let totalUsed = 0;
    let usedCount = 0;

    filteredLogs.forEach((row) => {
      const amount = trackEffectiveBalance
        ? Number(row.effective_amount || 0)
        : Number(row.base_amount || 0);
      const delta = trackEffectiveBalance ? getEffectiveDelta(row, companyId) : amount;

      if (trackEffectiveBalance) {
        if (delta > 0) {
          totalGiven += delta;
          givenCount += 1;
        } else if (delta < 0) {
          totalUsed += Math.abs(delta);
          usedCount += 1;
        }
        return;
      }

      if (row.action === 'addition') {
        totalGiven += amount;
        givenCount += 1;
      } else if (row.action === 'deduction') {
        totalUsed += amount;
        usedCount += 1;
      }
    });

    return {
      totalGiven,
      givenCount,
      totalUsed,
      usedCount,
      totalTransactions: filteredLogs.length,
    };
  }, [filteredLogs, trackEffectiveBalance, companyId]);

  const effectiveBalanceByLogId = useMemo(() => {
    if (!trackEffectiveBalance || !companyId) return new Map();
    const sorted = [...filteredLogs].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at),
    );
    let running = 0;
    const map = new Map();
    sorted.forEach((row) => {
      running += getEffectiveDelta(row, companyId);
      map.set(row.id, running);
    });
    return map;
  }, [filteredLogs, trackEffectiveBalance, companyId]);

  const totalPages = Math.max(1, Math.ceil(filteredLogs.length / rowsPerPage));
  const paginatedLogs = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredLogs.slice(start, start + rowsPerPage);
  }, [filteredLogs, currentPage, rowsPerPage]);

  const getCompanyRelativeAction = (row) => {
    if (isSuperAdmin) {
      return row.action === 'addition'
        ? { label: 'Credits Given', variant: 'given' }
        : { label: 'Credits Deducted', variant: 'deducted' };
    }

    const cid = Number(companyId);
    const isReceiver = Number(row.to_company_id) === cid;
    const isSender = Number(row.from_company_id) === cid;

    if (isReceiver) {
      return row.action === 'addition'
        ? { label: 'Credit Received', variant: 'given' }
        : { label: 'Credit Deducted', variant: 'deducted' };
    }
    if (isSender) {
      return row.action === 'addition'
        ? { label: 'Credit Given', variant: 'deducted' }
        : { label: 'Credit Received', variant: 'given' };
    }
    if (!row.from_company_id && isReceiver) {
      return { label: 'Credit Received', variant: 'given' };
    }
    return {
      label: row.action === 'addition' ? 'Addition' : 'Deduction',
      variant: row.action === 'addition' ? 'given' : 'deducted',
    };
  };

  const givenToCompanyBody = (row) =>
    row.to_company_name || row.toCompany?.name || displayName || '-';

  const counterpartyBody = (row) => {
    const cid = Number(companyId);
    if (Number(row.to_company_id) === cid) {
      return row.from_company_name || row.fromCompany?.name || 'System / Superadmin';
    }
    if (Number(row.from_company_id) === cid) {
      return row.to_company_name || row.toCompany?.name || '-';
    }
    return '-';
  };

  const handleFilter = () => {
    fetchLogs(fromDateValue, toDateValue);
    fetchCompanyBalance();
  };

  const handleReset = () => {
    const from = getOneMonthAgoStr();
    const to = getTodayStr();
    setFromDateValue(from);
    setToDateValue(to);
    setSearchTerm('');
    setCurrentPage(1);
    fetchLogs(from, to);
    fetchCompanyBalance();
  };

  const handleExport = () => {
    const exportRows = filteredLogs.map((row) => {
      const rel = getCompanyRelativeAction(row);
      const base = {
        Date: formatTableDateTime(row.created_at),
        Type: rel.label,
        'Performed By': row.performed_by_username || row.performedByUser?.username || '-',
        'Base Amount': row.base_amount,
        Multiplier: `${row.multiplier}x`,
      };
      if (trackEffectiveBalance) {
        base['Effective Amount'] = row.effective_amount;
        base['Effective Left'] = effectiveBalanceByLogId.get(row.id) ?? '-';
      }
      if (isSuperAdmin) {
        return { ...base, 'Given To Company': givenToCompanyBody(row) };
      }
      return { ...base, Counterparty: counterpartyBody(row) };
    });
    exportToCSV(
      exportRows,
      `${exportFilePrefix}-${displayName || companyId}`,
    );
  };

  const showingFrom = filteredLogs.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const showingTo = Math.min(currentPage * rowsPerPage, filteredLogs.length);

  if (loading && logsList.length === 0) return <Loader />;

  return (
    <div className="vch-root">
      <style>{`
        .vch-root { font-family: inherit; }
        .vch-toolbar { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 12px; margin-bottom: 16px; }
        .vch-toolbar__dates { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 12px; flex: 1; }
        .vch-date-field label { display: block; font-size: 12px; font-weight: 600; color: #64748b; margin-bottom: 4px; }
        .vch-date-field input { width: 150px; padding: 8px 12px; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 14px; color: #334155; }
        .vch-btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: 8px; font-size: 14px; font-weight: 500; cursor: pointer; border: none; transition: all 0.15s; }
        .vch-btn--primary { background: linear-gradient(135deg, #4f6edb, #3b5ccc); color: white; }
        .vch-btn--primary:hover { opacity: 0.92; }
        .vch-btn--outline { background: white; color: #4f6edb; border: 1px solid #4f6edb; }
        .vch-btn--export { background: white; color: #334155; border: 1px solid #e2e8f0; margin-left: auto; }
        .vch-search-wrap { position: relative; margin-bottom: 20px; }
        .vch-search-wrap svg { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #94a3b8; }
        .vch-search-wrap input { width: 100%; padding: 12px 14px 12px 42px; border: 1px solid #e2e8f0; border-radius: 10px; font-size: 14px; outline: none; }
        .vch-search-wrap input:focus { border-color: #4f6edb; box-shadow: 0 0 0 3px rgba(79, 110, 219, 0.12); }
        .vch-summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 24px; }
        @media (max-width: 1100px) { .vch-summary-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 600px) { .vch-summary-grid { grid-template-columns: 1fr; } }
        .vch-summary-card { display: flex; align-items: flex-start; gap: 14px; padding: 18px; background: white; border: 1px solid #e8edf5; border-radius: 12px; box-shadow: 0 1px 3px rgba(0,0,0,0.04); }
        .vch-summary-card__icon { width: 44px; height: 44px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .vch-summary-card__label { font-size: 13px; color: #64748b; margin-bottom: 4px; }
        .vch-summary-card__value { font-size: 22px; font-weight: 700; color: #1e293b; line-height: 1.2; }
        .vch-summary-card__sub { font-size: 12px; color: #94a3b8; margin-top: 4px; }
        .vch-table-wrap { border: 1px solid #e8edf5; border-radius: 12px; overflow: hidden; background: white; }
        .vch-table-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .vch-table { width: 100%; min-width: 960px; border-collapse: collapse; font-size: 14px; }
        .vch-table thead th { background: #f8fafc; color: #64748b; font-weight: 600; font-size: 12px; text-transform: uppercase; letter-spacing: 0.03em; padding: 14px 16px; text-align: left; border-bottom: 1px solid #e8edf5; white-space: nowrap; }
        .vch-table thead th.vch-col-amount { text-align: right; }
        .vch-table tbody td { padding: 14px 16px; border-bottom: 1px solid #f1f5f9; color: #334155; vertical-align: middle; }
        .vch-table tbody td.vch-col-amount { text-align: right; white-space: nowrap; }
        .vch-effective { color: #16a34a; font-weight: 600; }
        .vch-balance-after { color: #1e40af; font-weight: 700; min-width: 120px; }
        .vch-table tbody tr:last-child td { border-bottom: none; }
        .vch-table tbody tr:hover { background: #fafbfc; }
        .vch-badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; }
        .vch-badge--given { background: #dcfce7; color: #16a34a; }
        .vch-badge--deducted { background: #fee2e2; color: #dc2626; }
        .vch-user-cell { display: flex; align-items: center; gap: 10px; }
        .vch-avatar { width: 32px; height: 32px; border-radius: 50%; background: linear-gradient(135deg, #4f6edb, #7c3aed); color: white; font-size: 11px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .vch-company-cell { display: flex; align-items: center; gap: 8px; color: #334155; }
        .vch-company-cell svg { color: #94a3b8; flex-shrink: 0; }
        .vch-footer { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 12px; padding: 14px 16px; border-top: 1px solid #e8edf5; background: #fafbfc; font-size: 13px; color: #64748b; }
        .vch-pagination { display: flex; align-items: center; gap: 4px; }
        .vch-page-btn { width: 32px; height: 32px; border: 1px solid #e2e8f0; background: white; border-radius: 8px; display: flex; align-items: center; justify-content: center; cursor: pointer; color: #64748b; }
        .vch-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .vch-page-btn--active { background: #4f6edb; border-color: #4f6edb; color: white; font-weight: 600; }
        .vch-rows-select { padding: 6px 10px; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 13px; color: #334155; background: white; }
        .vch-empty { text-align: center; padding: 48px 16px; color: #94a3b8; }
      `}</style>

      <div className="vch-toolbar">
        <div className="vch-toolbar__dates">
          <div className="vch-date-field">
            <label>From</label>
            <CalendarDateInput
              value={fromDateValue}
              onChange={(e) => setFromDateValue(e)}
            />
          </div>
          <div className="vch-date-field">
            <label>To</label>
            <CalendarDateInput
              value={toDateValue}
              onChange={(e) => setToDateValue(e)}
            />
          </div>
          <button type="button" className="vch-btn vch-btn--primary" onClick={handleFilter}>
            <Filter size={16} />
            Filter
          </button>
          <button type="button" className="vch-btn vch-btn--outline" onClick={handleReset}>
            <RotateCcw size={16} />
            Reset
          </button>
        </div>
        {filteredLogs.length > 0 && (
          <button type="button" className="vch-btn vch-btn--export" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
        )}
      </div>

      <div className="vch-search-wrap">
        <Search size={18} />
        <input
          type="text"
          placeholder="Search your credit actions..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
        />
      </div>

      <div className="vch-summary-grid">
        <SummaryCard
          icon={Wallet}
          iconBg="#dcfce7"
          iconColor="#16a34a"
          label={trackEffectiveBalance ? 'Effective Received' : 'Total Credits Given'}
          value={formatRupeeAmount(summary.totalGiven)}
          subLabel={`${summary.givenCount} Transaction${summary.givenCount !== 1 ? 's' : ''}`}
        />
        <SummaryCard
          icon={CircleCheck}
          iconBg="#dbeafe"
          iconColor="#2563eb"
          label={trackEffectiveBalance ? 'Effective Used' : 'Total Used'}
          value={formatRupeeAmount(summary.totalUsed)}
          subLabel={`${summary.usedCount} Transaction${summary.usedCount !== 1 ? 's' : ''}`}
        />
        <SummaryCard
          icon={Scale}
          iconBg="#ede9fe"
          iconColor="#7c3aed"
          label={trackEffectiveBalance ? 'Effective Balance Left' : 'Current Balance'}
          value={formatRupeeAmount(currentBalance)}
          subLabel={`As on ${formatDisplayDate(toDateValue)}`}
        />
        <SummaryCard
          icon={TrendingUp}
          iconBg="#ffedd5"
          iconColor="#ea580c"
          label="Total Transactions"
          value={summary.totalTransactions}
          subLabel="This Period"
        />
      </div>

      <div className="vch-table-wrap">
        <div className="vch-table-scroll">
          <table className="vch-table">
            <thead>
              <tr>
                <th>Date &amp; Time</th>
                <th>Type</th>
                <th>Performed By</th>
                <th>{isSuperAdmin ? 'Given To Company' : 'Counterparty'}</th>
                <th className="vch-col-amount">Base Amount</th>
                <th className="vch-col-amount">Multiplier</th>
                {trackEffectiveBalance && (
                  <>
                    <th className="vch-col-amount">Effective Amount</th>
                    <th className="vch-col-amount">Effective Left</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {paginatedLogs.length === 0 ? (
                <tr>
                  <td colSpan={trackEffectiveBalance ? 8 : 6} className="vch-empty">
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                paginatedLogs.map((row) => {
                  const rel = getCompanyRelativeAction(row);
                  const username =
                    row.performed_by_username || row.performedByUser?.username || '-';
                  const companyLabel = isSuperAdmin
                    ? givenToCompanyBody(row)
                    : counterpartyBody(row);

                  return (
                    <tr key={row.id}>
                      <td>{formatTableDateTime(row.created_at)}</td>
                      <td>
                        <span className={`vch-badge vch-badge--${rel.variant}`}>
                          {rel.label}
                        </span>
                      </td>
                      <td>
                        <div className="vch-user-cell">
                          <span className="vch-avatar">{getInitials(username)}</span>
                          <span>{username}</span>
                        </div>
                      </td>
                      <td>
                        <div className="vch-company-cell">
                          <Building2 size={16} />
                          <span>{companyLabel}</span>
                        </div>
                      </td>
                      <td className="vch-col-amount">{formatRupeeAmount(row.base_amount)}</td>
                      <td className="vch-col-amount">{row.multiplier}x</td>
                      {trackEffectiveBalance && (
                        <>
                          <td className="vch-col-amount vch-effective">
                            {formatRupeeAmount(row.effective_amount)}
                          </td>
                          <td className="vch-col-amount vch-balance-after">
                            {formatRupeeAmount(effectiveBalanceByLogId.get(row.id))}
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="vch-footer">
          <span>
            Showing {showingFrom} to {showingTo} of {filteredLogs.length} entries
          </span>

          <div className="vch-pagination">
            <button
              type="button"
              className="vch-page-btn"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage(1)}
            >
              <ChevronsLeft size={16} />
            </button>
            <button
              type="button"
              className="vch-page-btn"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft size={16} />
            </button>
            <button type="button" className="vch-page-btn vch-page-btn--active">
              {currentPage}
            </button>
            <button
              type="button"
              className="vch-page-btn"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            >
              <ChevronRight size={16} />
            </button>
            <button
              type="button"
              className="vch-page-btn"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(totalPages)}
            >
              <ChevronsRight size={16} />
            </button>
          </div>

          <select
            className="vch-rows-select"
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value={10}>10 per page</option>
            <option value={15}>15 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
          </select>
        </div>
      </div>
    </div>
  );
}
